import React from "react";
import { Switch, Route, useLocation } from "wouter";
import { lazy, Suspense, useEffect, useState } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider } from "@/context/AppContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { AuthProvider } from "@/context/AuthContext";
import BottomNav from "@/components/layout/BottomNav";
import Header from "@/components/layout/Header";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Capacitor } from '@capacitor/core';  // Capacitor core
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar } from '@capacitor/status-bar';

const Home = lazy(() => import("@/pages/Home"));
const Community = lazy(() => import("@/pages/Community"));
const Track = lazy(() => import("@/pages/Track"));
const Resources = lazy(() => import("@/pages/Resources"));
const DailyTips = lazy(() => import("@/pages/DailyTips"));
const SoundTherapy = lazy(() => import("@/pages/Meditations"));
const PrivacySecurity = lazy(() => import("@/pages/PrivacySecurity"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const CrisisResources = lazy(() => import("@/pages/CrisisResources"));
const Profile = lazy(() => import("@/pages/Profile"));
const EditProfile = lazy(() => import("@/pages/EditProfile"));
const Auth = lazy(() => import("@/pages/Auth"));
const Messages = lazy(() => import("@/pages/Messages"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const NotFound = lazy(() => import("@/pages/not-found"));
const MoodTracker = lazy(() => import("@/components/modals/MoodTracker"));
const PanicMode = lazy(() => import("@/components/modals/PanicMode"));
const TaskCustomizer = lazy(() => import("@/components/modals/TaskCustomizer"));
const AgeGate = lazy(() => import("@/components/modals/AgeGate"));
const WelcomeTutorial = lazy(() => import("@/components/modals/WelcomeTutorial"));

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/auth" component={Auth} />
      <Route path="/daily-tips" component={DailyTips} />
      <Route path="/sound-therapy" component={SoundTherapy} />
      <Route path="/privacy-security" component={PrivacySecurity} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/crisis-resources" component={CrisisResources} />
      {/* Keep old route for compatibility */}
      <Route path="/meditations" component={SoundTherapy} />
      
      {/* Protected routes - require authentication */}
      <ProtectedRoute path="/community" component={Community} />
      <ProtectedRoute path="/track" component={Track} />
      <ProtectedRoute path="/profile" component={Profile} />
      <ProtectedRoute path="/edit-profile" component={EditProfile} />
      <ProtectedRoute path="/messages" component={Messages} />
      <ProtectedRoute path="/notifications" component={Notifications} />
      <ProtectedRoute path="/resources" component={Resources} />
      
      {/* Keep support route for backwards compatibility */}
      <Route path="/support" component={Resources} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const [location] = useLocation();
  const isAuth = location.startsWith('/auth');
  const [deferModals, setDeferModals] = useState(false);
  
  useEffect(() => {
    const initNative = async () => {
      if (Capacitor.isNativePlatform()) {
        // iOS: No-op (safe), Android: Disables overlay
        await StatusBar.setOverlaysWebView({ overlay: false });  
        
        // Both platforms
        PushNotifications.register();
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Push received: ', notification);
        });
      }
    };
    
    initNative().catch(console.error);
  }, []);
 
  useEffect(() => {
    const id = window.requestIdleCallback ? window.requestIdleCallback(() => setDeferModals(true)) : window.setTimeout(() => setDeferModals(true), 2000);
    return () => {
      if (typeof id === 'number') window.clearTimeout(id as number);
      // requestIdleCallback cancellation not strictly necessary for our one-shot usage
    };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <AppProvider>
            <NotificationProvider>
              <div className="min-h-screen flex flex-col relative app-gradient bg-surface font-inter text-navy antialiased">
                <Toaster />
                <main
                  id="main-content"
                  role="main"
                  className={`${isAuth ? 'max-w-5xl' : 'max-w-md'} mx-auto w-full px-4 content-container`}
                >
                  <Suspense fallback={<div className="p-4 text-center text-gray-500">Loading…</div>}>
                    <Router />
                  </Suspense>
                </main>
                {!isAuth && <BottomNav />}
                {deferModals && (
                  <Suspense>
                    <AgeGate />
                    <WelcomeTutorial />
                    <MoodTracker />
                    <PanicMode />
                    <TaskCustomizer />
                  </Suspense>
                )}
              </div>
            </NotificationProvider>
          </AppProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
